'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '../../../utils/cn';

const tabsListVariants = cva('items-center', {
  variants: {
    variant: {
      default: 'bg-muted text-muted-foreground inline-flex h-9 justify-center rounded-lg p-1',
      pill: 'flex gap-1',
      segmented: 'bg-surface-2 flex gap-0.5 rounded-full p-0.75',
    },
  },
  defaultVariants: { variant: 'default' },
});

const tabsTriggerVariants = cva(
  'focus-visible:outline-ring inline-flex items-center justify-center whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'ring-offset-background focus-visible:ring-ring data-[state=active]:bg-background data-[state=active]:text-foreground rounded-md px-3 py-1 text-sm font-medium transition-all focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none data-[state=active]:shadow',
        pill: 'text-fg-2 hover:text-fg-1 data-[state=active]:bg-surface-2 data-[state=active]:text-fg-1 h-8.5 rounded-full px-3.5 text-sm font-medium transition-colors',
        segmented:
          'text-fg-2 hover:text-fg-1 data-[state=active]:bg-surface-1 data-[state=active]:text-fg-1 data-[state=active]:ring-stroke h-8.5 flex-1 rounded-full px-3 text-sm font-medium transition-colors data-[state=active]:ring-1 data-[state=active]:ring-inset',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

/**
 * How a set of tabs looks: `default` (the grey bar), `pill` or `segmented`.
 */
type TabsVariant = NonNullable<VariantProps<typeof tabsListVariants>['variant']>;

const TabsVariantContext = React.createContext<TabsVariant>('default');

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
    /**
     * How the tabs look; every trigger inside takes the same look.
     */
    variant?: TabsVariant;
  }
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant }), className)}
      data-variant={variant}
      {...props}
    />
  </TabsVariantContext.Provider>
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'ring-offset-background focus-visible:ring-ring mt-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent, type TabsVariant };
